import { useCallback, useRef, useState } from 'react';
import { SlapActivity, SlapId, PlaceId, StickerDesignId, PublishActivity, Activity } from './Primitives';
import { getStickerDesign } from '../stickers/StickerDesigns';
import { Board } from './Primitives';
import { MutableRefObject } from 'react';
import type { ResourceCheckResult } from './useServerResourceCheck';

export interface EnhancedSlapActivity extends SlapActivity {
  resourceStatus?: 'loading' | 'available' | 'error' | 'timeout' | null;
  resourceError?: string;
  resourceAttempts?: number;
  resourceTimeElapsed?: number;
}

export type EnhancedActivity = EnhancedSlapActivity | PublishActivity;

export interface SlapResourceTracker {
  slapId: SlapId;
  placeId: PlaceId;
  startTime: number;
  isUserCreated: boolean; // Whether this slap was created by current user
  hasReceivedFirst202: boolean; // Whether we've received the first 202 response
}

/**
 * Enhanced slap activity management that integrates resource locator polling
 * for user-created slaps while maintaining backward compatibility with 
 * websocket-based activities
 */
export function useEnhancedSlapActivity(
  boardRef: MutableRefObject<Board>,
  activities: EnhancedActivity[],
  setActivities: (updater: (prev: EnhancedActivity[]) => EnhancedActivity[]) => void,
  indexingSlaps: Set<SlapId>,
  setIndexingSlaps: (updater: (prev: Set<SlapId>) => Set<SlapId>) => void,
  playerAddress?: string,
  onSlapSyncComplete?: (slapId: SlapId, placeId: PlaceId) => void
) {
  // Track slaps that should use resource locator (user-created ones)
  const [resourceTrackedSlaps, setResourceTrackedSlaps] = useState<Set<SlapId>>(new Set());
  const resourceTrackersRef = useRef<Map<SlapId, SlapResourceTracker>>(new Map());

  /**
   * Add a slap activity - enhanced version that can trigger resource locator
   * for user-created slaps
   */
  const addEnhancedSlapActivity = useCallback((
    slapId: SlapId, 
    placeId: PlaceId, 
    stickerId: StickerDesignId, 
    size: number,
    isUserCreated: boolean = false
  ) => {
    console.log(`Adding enhanced slap activity: ${slapId}, user-created: ${isUserCreated}`);
    
    // Get the sticker design to access its imageCID
    const stickerDesign = getStickerDesign({ boardRef, stickerId });
    
    const newActivity: EnhancedSlapActivity = {
      id: `slap-${Date.now()}`,
      timestamp: Date.now(),
      type: 'slap',
      slapId: Number(slapId),
      placeId,
      stickerId: Number(stickerId),
      size,
      imageCID: stickerDesign?.imageCID || '',
      resourceStatus: isUserCreated ? 'loading' : null,
      resourceAttempts: 0,
      resourceTimeElapsed: 0
    };

    // If this is user-created, set up resource tracking
    if (isUserCreated) {
      setResourceTrackedSlaps(prev => {
        const next = new Set(prev);
        next.add(slapId);
        return next;
      });
      resourceTrackersRef.current.set(slapId, {
        slapId,
        placeId,
        startTime: Date.now(),
        isUserCreated: true,
        hasReceivedFirst202: false
      });
      
      // Add to indexing set for immediate feedback
      setIndexingSlaps(prev => {
        const next = new Set(prev);
        next.add(slapId);
        return next;
      });
    }

    setActivities(prev => {
      // Check if activity already exists and update it
      const existingIndex = prev.findIndex(activity => 
        activity.type === 'slap' && 
        Number(activity.slapId) === Number(slapId)
      );
      if (existingIndex !== -1) {
        const updated = [...prev];
        updated[existingIndex] = { ...updated[existingIndex], ...newActivity };
        return updated;
      }
      return [newActivity, ...prev].slice(0, 10);
    });

    // Auto-dismiss activity after 30 seconds
    setTimeout(() => {
      setActivities(prev => prev.filter(activity => 
        !(activity.type === 'slap' && Number(activity.slapId) === Number(slapId))
      ));
    }, 30000);
  }, [boardRef, setActivities, setIndexingSlaps, setResourceTrackedSlaps]);

  /**
   * Update resource status for a specific slap
   */
  const updateSlapResourceStatus = useCallback((
    slapId: SlapId,
    result: ResourceCheckResult
  ) => {
    console.log(`Updating resource status for slap ${slapId}:`, result);
    
    // Get tracker info to check if this is the first 202
    const tracker = resourceTrackersRef.current.get(slapId);
    
    // Check if this is the first 202 response (slap is synced to server)
    if (result.status === 'loading' && tracker && !tracker.hasReceivedFirst202) {
      console.log(`🎉 First 202 received for slap ${slapId} - triggering sync complete!`);
      
      // Mark that we've received the first 202
      resourceTrackersRef.current.set(slapId, {
        ...tracker,
        hasReceivedFirst202: true
      });
      
      // Update activity status to 'available' since sync is complete
      setActivities(prev => prev.map(activity => {
        if (activity.type === 'slap' && Number((activity as EnhancedSlapActivity).slapId) === Number(slapId)) {
          return {
            ...(activity as EnhancedSlapActivity),
            resourceStatus: 'available', // Mark as available since slap is synced
            resourceError: undefined,
            resourceAttempts: result.attempts,
            resourceTimeElapsed: result.totalTimeElapsed
          } as EnhancedSlapActivity;
        }
        return activity;
      }));
      
      // Trigger sync completion callback immediately
      if (onSlapSyncComplete) {
        onSlapSyncComplete(slapId, tracker.placeId);
      }
      
      // Remove from indexing and tracking since we're done
      setIndexingSlaps(prev => {
        const next = new Set(prev);
        next.delete(slapId);
        return next;
      });

      setResourceTrackedSlaps(prev => {
        const next = new Set(prev);
        next.delete(slapId);
        return next;
      });

      resourceTrackersRef.current.delete(slapId);
      return;
    }

    // For ongoing loading states or other statuses, update normally
    setActivities(prev => prev.map(activity => {
      if (activity.type === 'slap' && Number((activity as EnhancedSlapActivity).slapId) === Number(slapId)) {
        return {
          ...(activity as EnhancedSlapActivity),
          resourceStatus: result.status,
          resourceError: result.error,
          resourceAttempts: result.attempts,
          resourceTimeElapsed: result.totalTimeElapsed
        } as EnhancedSlapActivity;
      }
      return activity;
    }));

    // Handle other completion states (error/timeout/final success)
    if (result.status === 'available' || result.status === 'error' || result.status === 'timeout') {
      setIndexingSlaps(prev => {
        const next = new Set(prev);
        next.delete(slapId);
        return next;
      });

      setResourceTrackedSlaps(prev => {
        const next = new Set(prev);
        next.delete(slapId);
        return next;
      });

      resourceTrackersRef.current.delete(slapId);

      // Only trigger callback if we haven't already done so on first 202
      if (tracker && !tracker.hasReceivedFirst202 && onSlapSyncComplete) {
        if (result.status === 'available') {
          onSlapSyncComplete(slapId, tracker.placeId);
        }
      }
    }
  }, [setActivities, setIndexingSlaps, setResourceTrackedSlaps, onSlapSyncComplete]);

  /**
   * Handle completion of resource locator polling
   */
  const handleResourceComplete = useCallback((slapId: SlapId) => (result: ResourceCheckResult) => {
    console.log(`Server resource check completed for slap ${slapId}:`, result);
    updateSlapResourceStatus(slapId, result);
  }, [updateSlapResourceStatus]);

  /**
   * Handle server resource check errors
   */
  const handleResourceError = useCallback((slapId: SlapId) => (result: ResourceCheckResult) => {
    console.log(`Server resource check error for slap ${slapId}:`, result);
    updateSlapResourceStatus(slapId, result);
  }, [updateSlapResourceStatus]);

  /**
   * Check if a slap should show enhanced indexing status
   */
  const getSlapIndexingStatus = useCallback((slapId: SlapId): {
    isIndexing: boolean;
    status: string;
    showProgress: boolean;
  } => {
    const isTracked = resourceTrackedSlaps.has(slapId);
    const isInIndexingSet = indexingSlaps.has(slapId);
    
    if (isTracked) {
      const activity = activities.find(a => a.type === 'slap' && (a as EnhancedSlapActivity).slapId === slapId) as EnhancedSlapActivity;
      if (activity?.resourceStatus) {
        switch (activity.resourceStatus) {
          case 'loading':
            return { 
              isIndexing: true, 
              status: `Fetching slap #${slapId}`, 
              showProgress: true 
            };
          case 'available':
            return { 
              isIndexing: false, 
              status: 'Available', 
              showProgress: false 
            };
          case 'error':
            return { 
              isIndexing: false, 
              status: `Error: ${activity.resourceError || 'Unknown error'}`, 
              showProgress: false 
            };
          case 'timeout':
            return { 
              isIndexing: false, 
              status: 'Timed out waiting for resource', 
              showProgress: false 
            };
        }
      }
      return { isIndexing: true, status: 'Starting resource check...', showProgress: true };
    }
    
    // Fallback to original websocket-based indexing
    if (isInIndexingSet) {
      return { isIndexing: true, status: 'Indexing...', showProgress: false };
    }
    
    return { isIndexing: false, status: '', showProgress: false };
  }, [resourceTrackedSlaps, indexingSlaps, activities]);

  return {
    addEnhancedSlapActivity,
    updateSlapResourceStatus,
    handleResourceComplete,
    handleResourceError,
    getSlapIndexingStatus,
    resourceTrackedSlaps: Array.from(resourceTrackedSlaps)
  };
}
